import type {
  CorrectiveActionStatus,
  MaintenanceStatus,
  NotificationView,
  QualityRecordStatus,
} from '@lims/shared-types';
import type { Tone } from '../../components/ui/index.js';

/**
 * Phase 12 presentation helpers — tones and the notification-to-page map.
 *
 * A TONE IS NOT A VERDICT. "Closed" is success-toned because the matter was
 * handled, not because any material passed (CLAUDE.md section 8).
 */

export function qualityStatusTone(status: QualityRecordStatus): Tone {
  switch (status) {
    case 'open':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'closed':
      return 'success';
    case 'cancelled':
      return 'neutral';
  }
}

export function actionStatusTone(status: CorrectiveActionStatus): Tone {
  switch (status) {
    case 'open':
      return 'warning';
    case 'completed':
      return 'info';
    case 'verified':
      return 'success';
    case 'cancelled':
      return 'neutral';
  }
}

export function maintenanceStatusTone(status: MaintenanceStatus): Tone {
  switch (status) {
    case 'planned':
      return 'info';
    case 'reported':
      return 'warning';
    case 'done':
    case 'resolved':
      return 'success';
    case 'cancelled':
      return 'neutral';
  }
}

/**
 * The page a notification leads to (D169). The server names the work item;
 * this decides only which page shows it. A kind the interface does not know
 * leads nowhere rather than somewhere wrong.
 */
export function notificationTarget(notification: Pick<NotificationView, 'entityType' | 'entityId' | 'parentId'>): string | null {
  switch (notification.entityType) {
    case 'test_assignment':
      return `/work/${notification.entityId}`;
    case 'test_attempt':
      return `/attempts/${notification.entityId}`;
    case 'equipment':
      return `/equipment/${notification.entityId}`;
    case 'maintenance_record':
      return `/maintenance/${notification.entityId}`;
    case 'report':
      return `/reports/${notification.entityId}`;
    case 'quality_record':
      return `/quality/${notification.entityId}`;
    case 'corrective_action':
      return notification.parentId ? `/quality/${notification.parentId}` : '/corrective-actions';
    default:
      return null;
  }
}

/** The laboratory-agnostic "today" for a date field's default: the browser's
 *  calendar date. The SERVER re-checks every date against the laboratory's
 *  own today and refuses a future one. */
export function browserToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Wrap an interpolated value in Unicode first-strong isolates (FSI … PDI), so
 * an English name or a date inside an Arabic sentence keeps its own direction
 * and never reorders the words around it. Found by the Phase 12 browser
 * check: "{{name}}، {{date}}" drew as "2026/09/Laboratory Manager. 25".
 */
export function isolate(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : `⁨${String(value)}⁩`;
}
